import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from 'components/utils/LoadingSpinner';
import canvasAPI from 'apis/canvasAPI';

const CanvasView: React.FC = () => {
  const { canvasId } = useParams<{ canvasId: string }>();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const fetchCanvasContent = async () => {
      if (!canvasId) {
        setError('Canvas ID is required');
        setLoading(false);
        return;
      }

      try {
        const response = await canvasAPI.getCanvasContent(canvasId);
        if (response.error) {
          setError(response.error);
        } else if (response.data) {
          setHtmlContent(response.data);
        } else {
          setError('No content received');
        }
      } catch (err) {
        console.error('Error fetching canvas content:', err);
        setError('Failed to load canvas content');
      } finally {
        setLoading(false);
      }
    };

    fetchCanvasContent();
  }, [canvasId]);

  useEffect(() => {
    if (htmlContent && iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(htmlContent);
        doc.close();
      }
    }
  }, [htmlContent]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen flex-col">
        <div className="text-red-500 text-xl mb-4">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen">
      <iframe
        ref={iframeRef}
        className="w-full border-0"
        style={{ height: '100vh' }}
        title={`Canvas ${canvasId}`}
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
};

export default CanvasView;
